<template>
  <MainViewContainer>

    <div class="col-span-12">
      <!-- Locked thread banner -->
      <div v-if="currentThread && currentThread.is_locked" class="locked-banner">
        {{ $t('default.discussions.thread_locked') }}
      </div>

      <!-- Posts loading error -->
      <div v-if="postsError" class="error-banner">
        {{ $t('default.discussions.load_error') }}
      </div>

      <!-- Posts list -->
      <ul class="posts-list">
        <li
          v-for="post in posts"
          :key="post.id"
          class="post-item"
          :class="{ 'post-item--deleted': post.is_deleted, 'post-item--optimistic': post._optimistic }"
        >
          <template v-if="post.is_deleted">
            <div class="post-item__deleted">
              <em>{{ $t('default.discussions.post_deleted') }}</em>
            </div>
          </template>

          <template v-else>
            <div class="post-item__header">
              <div class="post-item__avatar">
                <img
                  v-if="post.author && post.author.avatar_url"
                  :src="post.author.avatar_url"
                  :alt="post.author.full_name"
                  class="avatar-img"
                />
                <div v-else class="avatar-initials">
                  {{ authorInitials(post.author) }}
                </div>
              </div>

              <div class="post-item__author-info">
                <span class="post-item__author-name">
                  {{ post.author ? post.author.full_name : $t('default.discussions.unknown_author') }}
                </span>
                <span class="post-item__timestamp">
                  {{ formatDate(post.created) }}
                  <span v-if="post.modified !== post.created" class="post-item__edited">
                    ({{ $t('default.discussions.edited') }})
                  </span>
                </span>
              </div>

              <div v-if="canEdit(post)" class="post-item__actions">
                <button class="small" @click="startEdit(post)">
                  {{ $t('default.discussions.edit') }}
                </button>
              </div>
            </div>

            <!-- Edit mode -->
            <div v-if="editingPostId === post.id" class="post-item__edit">
              <PostComposer
                :initial-body="post.body"
                :submitting="postSubmitting"
                :error="postSubmitError"
                submit-label="Save"
                @submit="submitEdit(post.id, $event)"
                @cancel="cancelEdit"
              />
            </div>

            <!-- Body -->
            <div v-else class="post-item__body">
              <MarkdownRenderer :content="post.body" />
            </div>

            <!-- Reaction bar -->
            <div class="post-item__reactions">
              <ReactionBar :post-id="post.id" :thread-id="threadId" />
            </div>
          </template>
        </li>
      </ul>

      <!-- Load more posts -->
      <div v-if="postsNextPage" class="load-more">
        <button
          class="green"
          :disabled="postsLoading"
          @click="loadMorePosts"
        >
          {{ postsLoading ? $t('default.discussions.loading') : $t('default.discussions.load_more_posts') }}
        </button>
      </div>

      <div v-if="postsLoading && posts.length === 0" class="loading-state">
        <Loader />
      </div>

      <!-- Reply composer (hidden if locked) -->
      <div
        v-if="currentThread && !currentThread.is_locked"
        class="reply-section"
      >
        <h3>{{ $t('default.discussions.reply_heading') }}</h3>
        <PostComposer
          :submitting="postSubmitting"
          :error="postSubmitError"
          :submit-label="$t('default.discussions.post_reply')"
          @submit="submitReply"
        />
      </div>
    </div>
  </MainViewContainer>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import MainViewContainer from '@/components/MainViewContainer'
import Loader from '@/components/Loader'
import MarkdownRenderer from '@/components/shared/MarkdownRenderer'
import PostComposer from '@/components/discussions/PostComposer'
import ReactionBar from '@/components/discussions/ReactionBar'
import { stringToDatetime } from '@/datetimeUtils'

const EDIT_WINDOW_MS = 10 * 60 * 1000

const store = useStore()
const route = useRoute()
const threadId = computed(() => route.params.threadId)

const currentThread = computed(() => store.state.discussions.currentThread)
const posts = computed(() => store.state.discussions.posts)
const postsLoading = computed(() => store.state.discussions.postsLoading)
const postsError = computed(() => store.state.discussions.postsError)
const postsNextPage = computed(() => store.state.discussions.postsNextPage)
const postSubmitting = computed(() => store.state.discussions.postSubmitting)
const postSubmitError = computed(() => store.state.discussions.postSubmitError)
const currentUser = computed(() => store.state.user?.user)

const editingPostId = ref(null)

function authorInitials(author) {
  if (!author) return '?'
  const name = author.full_name || ''
  return name
    .split(' ')
    .filter(Boolean)
    .map(w => w[0].toUpperCase())
    .slice(0, 2)
    .join('')
}

function formatDate(dateStr) {
  return stringToDatetime(dateStr)
}

function canEdit(post) {
  if (!currentUser.value || !post.author) return false
  if (currentUser.value.id !== post.author.id) return false
  const created = new Date(post.created)
  return Date.now() - created.getTime() < EDIT_WINDOW_MS
}

function startEdit(post) {
  editingPostId.value = post.id
}

function cancelEdit() {
  editingPostId.value = null
}

async function submitEdit(postId, body) {
  try {
    await store.dispatch('discussions/editPost', { threadId: threadId.value, postId, body })
    editingPostId.value = null
  } catch {
    // error shown via postSubmitError
  }
}

async function submitReply(body) {
  try {
    await store.dispatch('discussions/createPost', {
      threadId: threadId.value,
      body,
    })
  } catch {
    // error shown via postSubmitError
  }
}

function loadMorePosts() {
  store.dispatch('discussions/fetchMorePosts', threadId.value)
}

onMounted(() => {
  store.dispatch('discussions/fetchThread', threadId.value)
  store.dispatch('discussions/fetchPosts', threadId.value)
})
</script>

<style scoped>
.locked-banner {
  background: #fff5f5;
  border: 1px solid var(--halsa-red);
  border-radius: 6px;
  padding: 0.75rem 1rem;
  color: var(--halsa-red);
  margin-bottom: 1.5rem;
  font-weight: 600;
}

.error-banner {
  background: #fff5f5;
  border: 1px solid var(--halsa-red);
  border-radius: 6px;
  padding: 0.75rem 1rem;
  color: var(--halsa-red);
  margin-bottom: 1rem;
}

.posts-list {
  list-style: none;
  padding: 0;
  margin: 0 0 1.5rem 0;
}

.post-item {
  border: 1px solid var(--gray-200);
  border-radius: 8px;
  margin-bottom: 1rem;
  background: white;
  padding: 1rem 1.25rem;
}

.post-item--deleted {
  background: var(--gray-100);
  border-color: var(--gray-200);
  opacity: 0.7;
}

.post-item--optimistic {
  opacity: 0.6;
}

.post-item__deleted {
  color: var(--text-grey);
  font-size: 0.9rem;
}

.post-item__header {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  flex-wrap: wrap;
}

.post-item__avatar {
  flex-shrink: 0;
}

.avatar-img {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
}

.avatar-initials {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--halsa-blue);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.85rem;
  font-family: 'Raleway', sans-serif;
}

.post-item__author-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.post-item__author-name {
  font-weight: 700;
  font-size: 0.95rem;
}

.post-item__timestamp {
  font-size: 0.8rem;
  color: var(--text-grey);
}

.post-item__edited {
  font-style: italic;
}

.post-item__actions {
  flex-shrink: 0;
}

.post-item__body {
  padding-left: calc(40px + 0.75rem);
}

.post-item__reactions {
  padding-left: calc(40px + 0.75rem);
  margin-top: 0.5rem;
}

.post-item__edit {
  padding-left: calc(40px + 0.75rem);
}

.loading-state {
  display: flex;
  justify-content: center;
  padding: 2rem 0;
}

.load-more {
  display: flex;
  justify-content: center;
  margin-bottom: 1.5rem;
}

.reply-section {
  border-top: 2px solid var(--gray-200);
  padding-top: 1.5rem;
  margin-top: 0.5rem;
}

@media (max-width: 375px) {
  .post-item {
    padding: 0.75rem;
  }

  .post-item__body,
  .post-item__reactions,
  .post-item__edit {
    padding-left: 0;
  }

  .thread-header {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
